import React from 'react';
import { motion } from 'motion/react';
import { Users, MessageSquare, Clock, Star } from 'lucide-react';
import { fadeInUp, staggerContainer, staggerItem, hoverLift } from '../../lib/motion';

interface Stat {
  icon: React.ComponentType<{ className?: string }>;
  number: string;
  label: string;
}

interface Testimonial {
  text: string;
  name: string;
  role: string;
  initials: string;
}

const SocialProof: React.FC = () => {
  const stats: Stat[] = [
    {
      icon: MessageSquare,
      number: '10K+',
      label: 'Messaggi processati'
    },
    {
      icon: Clock,
      number: '99%',
      label: 'Uptime garantito'
    },
    {
      icon: Star,
      number: '4.9/5',
      label: 'Rating clienti'
    },
    {
      icon: Users,
      number: '500+',
      label: 'Aziende attive'
    }
  ];

  const testimonials: Testimonial[] = [
    {
      text: "ChatLy ha rivoluzionato il nostro customer service. Ora gestiamo 3x più clienti con la metà dello staff, e la soddisfazione è aumentata del 40%.",
      name: "Marco Civelli",
      role: "CEO, TechCorp",
      initials: "MC"
    },
    {
      text: "Incredibile! Le prenotazioni automatiche ci hanno fatto risparmiare 15 ore a settimana. I clienti adorano la facilità di prenotazione via WhatsApp.",
      name: "Sofia Rossi",
      role: "Proprietaria, Wellness Spa",
      initials: "SR"
    },
    {
      text: "Con ChatLy abbiamo automatizzato completamente gli ordini. +65% di vendite online e zero errori nelle comande. Fantastico!",
      name: "Giuseppe Bianchi",
      role: "Manager, PizzaExpress",
      initials: "GB"
    },
    {
      text: "I nostri pazienti possono prenotare visite 24/7. Abbiamo ridotto le chiamate dell'80% e migliorato l'esperienza paziente.",
      name: "Dr. Laura Verdi",
      role: "Direttrice, Clinica MedCenter",
      initials: "LV"
    },
    {
      text: "Setup velocissimo e supporto eccezionale. In una settimana eravamo operativi. I risultati? +150% lead qualificati!",
      name: "Andrea Neri",
      role: "Marketing Director, FinanceGo",
      initials: "AN"
    },
    {
      text: "Le nostre clienti adorano ricevere conferme e promemoria automatici. Abbiamo eliminato i no-show e aumentato la fedeltà.",
      name: "Chiara Esposito",
      role: "Founder, BeautyHub",
      initials: "CE"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="container mx-auto px-4">
        <motion.div
          {...fadeInUp}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            I numeri parlano <span className="text-primary">chiaro</span>
          </h2>
          <p className="text-text-secondary text-lg">
            Aziende italiane scelgono ChatLy ogni giorno per automatizzare le conversazioni su WhatsApp.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-20"
        >
          {stats.map((stat) => (
            <motion.div
              key={stat.label}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              transition={hoverLift}
              className="bg-card rounded-2xl border border-border p-6 text-center shadow-sm hover:shadow-md"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-primary/10 mb-4">
                <stat.icon className="h-6 w-6 text-primary" aria-hidden="true" />
              </div>
              <h3 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-1">
                {stat.number}
              </h3>
              <p className="text-text-secondary font-medium text-sm">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          {...fadeInUp}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <h3 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-4">
            Cosa dicono i nostri clienti
          </h3>
          <p className="text-text-secondary text-lg">
            Storie di successo reali da aziende che hanno trasformato il loro business.
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {testimonials.map((testimonial) => (
            <motion.div
              key={testimonial.name}
              variants={staggerItem}
              whileHover={{ y: -6 }}
              transition={hoverLift}
              className="bg-card rounded-2xl p-6 shadow-sm border border-border hover:shadow-md flex flex-col"
            >
              <div className="flex mb-4" aria-label="Valutazione 5 su 5">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 text-warning fill-current" aria-hidden="true" />
                ))}
              </div>
              <blockquote className="text-text-primary mb-6 text-sm lg:text-base leading-relaxed flex-1">
                {testimonial.text}
              </blockquote>
              <div className="flex items-center">
                <div className="w-11 h-11 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold text-sm mr-3">
                  {testimonial.initials}
                </div>
                <div>
                  <p className="font-semibold text-text-primary text-sm">{testimonial.name}</p>
                  <p className="text-text-secondary text-xs">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default SocialProof;
